import React, { useState, useEffect, useCallback } from "react";
import styles from "./VfcFirstMainPage.module.css";
import { useNavigate } from "react-router-dom";

import jfmc_img1 from "../../../assets/images/carousel/jfmc_img1.jpeg";
import banner_2 from "../../../assets/images/carousel/banner_2.jpeg";
import banner_3 from "../../../assets/images/carousel/banner_3.jpeg";
import banner_4 from "../../../assets/images/carousel/banner_4.jpeg";
import banner_5 from "../../../assets/images/carousel/banner_5.jpg";
import banner_6 from "../../../assets/images/carousel/banner_6.jpg";
import banner_7 from "../../../assets/images/carousel/banner_7.jpg";
import banner_8 from "../../../assets/images/carousel/banner_8.jpg";
import banner_9 from "../../../assets/images/carousel/banner_9.jpg";
import logoForest from "../../../assets/images/auth/logo_forest-1.png";

const SLIDES = [jfmc_img1, banner_2, banner_3, banner_4, banner_5];

const FOCUS_AREAS = [
  {
    img: banner_6,
    icon: "mdi mdi-account-group",
    title: "Community Partnership",
    desc: "Fostering collaborative relationships between local communities and forest administration for effective participatory forest management.",
  },
  {
    img: banner_7,
    icon: "mdi mdi-leaf",
    title: "Sustainable Management",
    desc: "Implementing scientific forest management practices that balance ecological conservation with sustainable livelihood development.",
  },
  {
    img: banner_9,
    icon: "mdi mdi-shield-check",
    title: "Forest Protection",
    desc: "Strengthening protection mechanisms through community vigilance, anti-poaching measures, and biodiversity conservation.",
  },
];

const VfcFirstMainPage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [jfmcWise, setJfmcWise] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const Api = process.env.REACT_APP_API;
  const navigate = useNavigate();
  const [ecoData, setEcoData] = useState({
    totalSites: 0,
    totalVisitors: 0,
    totalRevenue: 0,
    totalWorkers: 0,
  });
  /* Auto-advance carousel */
  const nextSlide = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % SLIDES.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  /* Fetch stats */
  useEffect(() => {
    const loadData = async () => {
      try {
        const [jfmcRes, ecoRes] = await Promise.all([
          fetch(`${Api}/api/state/counts`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          }),
          fetch(`${Api}/api/state/eco/counts`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          }),
        ]);

        const jfmcData = await jfmcRes.json();
        const ecoData = await ecoRes.json();

        setJfmcWise(jfmcData.jfmcWise || []);

        // API returns object not array
        setEcoData(ecoData);
      } catch (err) {
        console.error(err);
      }
    };

    loadData();
  }, [Api]);
  /* Aggregate totals across all JFMC types */
  const totals = jfmcWise.reduce(
    (acc, j) => ({
      vfcs: acc.vfcs + (j.totalVfcs || 0),
      members: acc.members + (j.totalMembers || 0),
      shgs: acc.shgs + (j.totalShgs || 0),
      shgMembers: acc.shgMembers + (j.totalShgMembers || 0),
    }),
    { vfcs: 0, members: 0, shgs: 0, shgMembers: 0 }
  );

  const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
  return (
    <div className={styles.page}>

      {/* ── Top Announcement Bar ── */}
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <span className={styles.topBarItem}>
            <i className="mdi mdi-email-outline" /> info@digitalvfc.com
          </span>
          <span className={styles.topBarItem}>
            <i className="mdi mdi-map-marker-outline" /> Tamil Nadu Forest Department
          </span>
        </div>
        <span className={styles.topBarItem}>
          <i className="mdi mdi-leaf" /> Protecting Tamil Nadu's Green Heritage
        </span>
      </div>

      {/* ── Fixed Navbar ── */}
      <nav className={styles.navbar}>
        <div className={styles.navBrand}>
          <div className={styles.navLogoWrap}>
            <img src={logoForest} alt="TNFD Logo" className={styles.navLogo} />
          </div>
          <div className={styles.navBrandText}>
            <span className={styles.navBrandName}>Tamil Nadu Forest Dept.</span>
            <span className={styles.navBrandSub}>JFMC Digital Platform</span>
          </div>
        </div>

        <ul className={`${styles.navLinks} ${mobileOpen ? styles.open : ""}`}>
          <li><a className={styles.navLink} href="#home" onClick={() => setMobileOpen(false)}>Home</a></li>
          <li><a className={styles.navLink} href="#about" onClick={() => setMobileOpen(false)}>About</a></li>
          <li><a className={styles.navLink} href="#focus" onClick={() => setMobileOpen(false)}>Focus Areas</a></li>
          <li><a className={styles.navLink} href="#stats" onClick={() => setMobileOpen(false)}>Statistics</a></li>
          <li><a className={styles.navLink} href="#contact" onClick={() => setMobileOpen(false)}>Contact</a></li>
        </ul>

        <button className={styles.loginBtn} onClick={() => navigate("/login-1")}>
          <i className="mdi mdi-login" /> Department Login
        </button>

        <button className={styles.hamburger} onClick={() => setMobileOpen((o) => !o)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </nav>

      {/* ── Hero Section ── */}
      <section className={styles.hero} id="home">
        {SLIDES.map((src, i) => (
          <div key={i} className={`${styles.heroSlide} ${i === activeSlide ? styles.active : ""}`}>
            <img src={src} alt={`Forest slide ${i + 1}`} />
          </div>
        ))}
        <div className={styles.heroOverlay} />

        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <i className="mdi mdi-leaf" /> Tamil Nadu Forest Department
          </div>

          <h1 className={styles.heroTitle}>
            Joint Forest <span>Management</span> Committee
          </h1>

          <p className={styles.heroSubtitle}>
            Empowering communities to protect, conserve, and sustainably manage
            Tamil Nadu's rich forest heritage through participatory governance.
          </p>

          <div className={styles.heroActions}>
            <button className={styles.heroPrimaryBtn} onClick={() => navigate("/login-1")}>
              <i className="mdi mdi-login" /> Department Login
            </button>
            <a className={styles.heroSecondaryBtn} href="#about">
              <i className="mdi mdi-information-outline" /> Learn More
            </a>
          </div>
        </div>

        {/* Slide dots */}
        <div className={styles.heroIndicators}>
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`${styles.heroDot} ${i === activeSlide ? styles.activeDot : ""}`}
              onClick={() => setActiveSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        <div className={styles.scrollHint}>
          <i className="mdi mdi-chevron-down" />
          <span>Scroll</span>
        </div>
      </section>

      {/* ── Stats Strip ── */}
      {/* JFMC Statistics */}
      <div className={styles.statsHeader}>
        <span className={styles.statsBadge}>
          <i className="mdi mdi-account-group" />
          JFMC Statistics
        </span>
        <h2 className="mt-3">Joint Forest Management Committees</h2>
        <p>Overall performance and community participation across Tamil Nadu</p>
      </div>

      <div className={styles.statsStrip}>
        <div className={styles.statsInner}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{fmt(totals.vfcs)}</div>
            <div className={styles.statLabel}>Active Committees</div>
            <div className={styles.statType}>JFMCs Across Tamil Nadu</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{fmt(totals.members)}</div>
            <div className={styles.statLabel}>Members</div>
            <div className={styles.statType}>Registered JFMC Members</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{fmt(totals.shgs)}</div>
            <div className={styles.statLabel}>Self Help Groups</div>
            <div className={styles.statType}>SHGs Under JFMC</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{fmt(totals.shgMembers)}</div>
            <div className={styles.statLabel}>SHG Members</div>
            <div className={styles.statType}>Women Empowered</div>
          </div>
        </div>
      </div>

      {/* ── ECO Tourism Stats Strip ── */}
      {/* ECO Tourism Statistics */}
      <div className={styles.statsHeader}>
        <span className={styles.statsBadge}>
          <i className="mdi mdi-forest" />
          Eco Tourism Statistics
        </span>
        <h2 className="mt-3">Eco Tourism Performance</h2>
        <p>Revenue, visitors, workers and tourism centres across Tamil Nadu</p>
      </div>
      <div className={styles.statsStrip}>
        <div className={styles.statsInner}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{fmt(ecoData.totalSites)}</div>
            <div className={styles.statLabel}>Active ECO Tourism's</div>
            <div className={styles.statType}>ECO Tourism's Across Tamil Nadu</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{fmt(ecoData.totalRevenue)}</div>
            <div className={styles.statLabel}>Total Revenue</div>
            <div className={styles.statType}>ECO Tourism's Revenue</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{fmt(ecoData.totalVisitors)}</div>
            <div className={styles.statLabel}>Total Visitors</div>
            <div className={styles.statType}>ECO Tourism's Visitors</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{fmt(ecoData.totalWorkers)}</div>
            <div className={styles.statLabel}>Total Workers</div>
            <div className={styles.statType}>ECO Tourism's Workers</div>
          </div>
        </div>
      </div>

      {/* ── About Section ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`} id="about">
        <div className={styles.container}>
          <div className={styles.aboutGrid}>
            {/* Right image stack (shown first on mobile) */}
            <div className={styles.imageStack}>
              <img className={styles.imgMain} src={jfmc_img1} alt="Forest Management" />
              <img className={styles.imgAccent} src={banner_8} alt="Community" />
              <div className={styles.imgBadge}>Est. 1856 · Tamil Nadu</div>
            </div>

            {/* Left content */}
            <div>
              <div className={styles.sectionTag}>
                <i className="mdi mdi-information-outline" /> About JFMC
              </div>
              <h2 className={styles.sectionTitle}>
                Guardians of Tamil Nadu's <span>Forest Heritage</span>
              </h2>
              <p className={styles.sectionDesc}>
                Tamil Nadu's forest management began with timber exploitation in the early 1800s and was
                formalized with the Forest Department in 1856. Strengthened by the Madras Forest Act of 1882
                and the National Forest Policy of 1988, it evolved from timber harvest to conservation —
                focusing on ecological security, wildlife protection, and community livelihoods.
              </p>

              <div className={styles.aboutPoints}>
                <div className={styles.aboutPoint}>
                  <div className={styles.aboutPointIcon}><i className="mdi mdi-nature" /></div>
                  <span><strong>Ecological Security</strong> — Protecting biodiversity and natural forest ecosystems across Tamil Nadu.</span>
                </div>
                <div className={styles.aboutPoint}>
                  <div className={styles.aboutPointIcon}><i className="mdi mdi-account-group" /></div>
                  <span><strong>Community Empowerment</strong> — Enabling villages to participate in and benefit from forest governance.</span>
                </div>
                <div className={styles.aboutPoint}>
                  <div className={styles.aboutPointIcon}><i className="mdi mdi-cash-multiple" /></div>
                  <span><strong>Livelihood Support</strong> — Providing financial support through revolving funds and SHG networks.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Focus Areas ── */}
      <section className={styles.section} id="focus">
        <div className={styles.container}>
          <div style={{ textAlign: "center", marginBottom: 0 }}>
            <div className={styles.sectionTag} style={{ margin: "0 auto 14px" }}>
              <i className="mdi mdi-target" /> Our Focus
            </div>
            <h2 className={styles.sectionTitle}>
              Official <span>Focus Areas</span>
            </h2>
            <p className={styles.sectionDesc} style={{ margin: "0 auto" }}>
              Three pillars that guide Tamil Nadu's Joint Forest Management towards a sustainable future.
            </p>
          </div>

          <div className={styles.cardsGrid}>
            {FOCUS_AREAS.map((area, i) => (
              <div className={styles.card} key={i}>
                <div className={styles.cardImgWrap}>
                  <img src={area.img} alt={area.title} className={styles.cardImg} />
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardIcon}>
                    <i className={area.icon} />
                  </div>
                  <div className={styles.cardTitle}>{area.title}</div>
                  <div className={styles.cardDesc}>{area.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── JFMC At a Glance ── */}
      {jfmcWise.length > 0 && (
        <section className={styles.glanceSection} id="stats">
          <div className={styles.container}>
            <div className={styles.glanceHeader}>
              <div className={styles.glanceSectionTag}>
                <i className="mdi mdi-chart-bar" /> Live Statistics
              </div>
              <h2 className={styles.glanceTitle}>JFMC at a Glance</h2>
              <p className={styles.glanceSubtitle}>Real-time data from across Tamil Nadu's forest management network</p>
            </div>

            {jfmcWise.map((j) => (
              <div className={styles.glanceGroup} key={j._id}>
                <div className={styles.glanceTypeTitle}>{j.jfmcTypeName}</div>
                <div className={styles.glanceCards}>
                  <div className={styles.glanceCard}>
                    <div className={styles.glanceCardValue}>{fmt(j.totalVfcs)}</div>
                    <div className={styles.glanceCardLabel}>Active Committees</div>
                  </div>
                  <div className={styles.glanceCard}>
                    <div className={styles.glanceCardValue}>{fmt(j.totalMembers)}</div>
                    <div className={styles.glanceCardLabel}>Members</div>
                  </div>
                  <div className={styles.glanceCard}>
                    <div className={styles.glanceCardValue}>{fmt(j.totalShgs)}</div>
                    <div className={styles.glanceCardLabel}>Self Help Groups</div>
                  </div>
                  <div className={styles.glanceCard}>
                    <div className={styles.glanceCardValue}>{fmt(j.totalShgMembers)}</div>
                    <div className={styles.glanceCardLabel}>SHG Members</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Footer ── */}
      <footer className={styles.footer} id="contact">
        <div className={styles.footerTop}>
          <div className={styles.footerBrand}>
            <div className={styles.footerLogoWrap}>
              <img src={logoForest} alt="Logo" className={styles.footerLogo} />
            </div>
            <div className={styles.footerBrandName}>Tamil Nadu Forest Department<br />JFMC Digital Platform</div>
            <div className={styles.footerBrandDesc}>
              Empowering Joint Forest Management Committees across Tamil Nadu through
              digital governance, transparent reporting, and community participation.
            </div>
          </div>

          <div>
            <div className={styles.footerColTitle}>Quick Links</div>
            <ul className={styles.footerLinks}>
              <li><a href="#home"><i className="mdi mdi-chevron-right" />Home</a></li>
              <li><a href="#about"><i className="mdi mdi-chevron-right" />About JFMC</a></li>
              <li><a href="#focus"><i className="mdi mdi-chevron-right" />Focus Areas</a></li>
              <li><a href="#stats"><i className="mdi mdi-chevron-right" />Statistics</a></li>
              <li><a href="#contact"><i className="mdi mdi-chevron-right" />Contact</a></li>
            </ul>
          </div>

          <div>
            <div className={styles.footerColTitle}>Contact Us</div>
            <div className={styles.footerContact}>
              <div className={styles.footerContactItem}>
                <i className="mdi mdi-email-outline" />
                <span>info@digitalvfc.com</span>
              </div>
              <div className={styles.footerContactItem}>
                <i className="mdi mdi-map-marker-outline" />
                <span>Tamil Nadu Forest Department,<br />Chennai, Tamil Nadu, India</span>
              </div>
              <div className={styles.footerContactItem}>
                <i className="mdi mdi-web" />
                <span>www.forests.tn.gov.in</span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <div className={styles.footerBottom}>
            <span>© 2026 Tamil Nadu Forest Department. All rights reserved.</span>
            <div className={styles.footerBottomRight}>
              <span>Designed &amp; Developed by <strong style={{ color: "#4CAF50" }}>Thrayam Tech</strong></span>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default VfcFirstMainPage;
